import React from 'react';
import ReactDOM from 'react-dom/client';
import html2canvas from 'html2canvas';
import { mmToPx } from '../dailyReport/pdfLayout';

const PDF_GUTTER = 36;
const DEFAULT_RENDER_WAIT_MS = 1500;
const DEFAULT_CONTENT_PADDING_PX = 6;

function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

function resolveCaptureWidthPx(contentWidthMm, options) {
  if (options.captureWidthPx != null && options.captureWidthPx > 0) {
    return Math.round(options.captureWidthPx);
  }
  if (typeof contentWidthMm === 'number' && contentWidthMm > 0) {
    return mmToPx(contentWidthMm);
  }
  return 880;
}

/**
 * Render a React component off-screen and embed it as a PNG in a jsPDF page.
 */
export async function renderReactSection(
  pdf,
  Component,
  props,
  container,
  margin,
  contentWidth,
  options = {}
) {
  const renderWaitMs = options.renderWaitMs ?? DEFAULT_RENDER_WAIT_MS;
  const topOffsetMm = options.topOffsetMm ?? 30;
  const captureWidthPx = resolveCaptureWidthPx(contentWidth, options);
  const contentPaddingPx = options.contentPaddingPx ?? DEFAULT_CONTENT_PADDING_PX;
  const minCaptureHeightPx = options.minCaptureHeightPx;
  const fixedCaptureHeightPx = options.fixedCaptureHeightPx;

  if (!container) {
    throw new Error('Print container element is required');
  }

  let root = null;
  let wrapper = null;

  try {
    container.innerHTML = '';

    wrapper = document.createElement('div');
    wrapper.style.width = `${captureWidthPx}px`;
    wrapper.style.padding = `${contentPaddingPx}px ${PDF_GUTTER}px ${contentPaddingPx}px`;
    wrapper.style.boxSizing = 'border-box';
    wrapper.style.background = '#ffffff';
    wrapper.style.visibility = 'visible';
    wrapper.style.opacity = '1';
    if (fixedCaptureHeightPx != null && fixedCaptureHeightPx > 0) {
      const heightPx = Math.round(fixedCaptureHeightPx);
      wrapper.style.height = `${heightPx}px`;
      wrapper.style.minHeight = `${heightPx}px`;
      wrapper.style.maxHeight = `${heightPx}px`;
      wrapper.style.overflow = 'hidden';
      wrapper.style.display = 'flex';
      wrapper.style.flexDirection = 'column';
    } else if (minCaptureHeightPx != null && minCaptureHeightPx > 0) {
      wrapper.style.minHeight = `${Math.round(minCaptureHeightPx)}px`;
      wrapper.style.display = 'flex';
      wrapper.style.flexDirection = 'column';
    }
    container.appendChild(wrapper);

    root = ReactDOM.createRoot(wrapper);
    root.render(React.createElement(Component, props));

    await waitForPaint();
    if (renderWaitMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, renderWaitMs));
    }

    const canvas = await html2canvas(wrapper, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: captureWidthPx,
      windowWidth: captureWidthPx,
      onclone: (_doc, clone) => {
        clone.style.visibility = 'visible';
        clone.style.opacity = '1';
      },
    });

    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const bottomReserve = options.bottomReserveMm ?? 15;
    const maxContentHeight = pageHeight - topOffsetMm - bottomReserve;

    let drawWidth = imgWidth;
    let drawHeight = imgHeight;

    if (drawHeight > maxContentHeight) {
      const scaleFactor = maxContentHeight / drawHeight;
      drawHeight = maxContentHeight;
      drawWidth = imgWidth * scaleFactor;
    }

    const xOffset = margin + Math.max(0, (contentWidth - drawWidth) / 2);

    if (drawHeight > maxContentHeight + 0.01 && process.env.NODE_ENV !== 'production') {
      console.info('[renderReactSection] Scaling section to fit page:', {
        imgHeight: imgHeight.toFixed(1),
        maxContentHeight: maxContentHeight.toFixed(1),
        drawWidth: drawWidth.toFixed(1),
        drawHeight: drawHeight.toFixed(1),
      });
    }

    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      xOffset,
      topOffsetMm,
      drawWidth,
      drawHeight
    );
  } catch (error) {
    console.error('Error rendering component to PDF:', error);
    pdf.setFontSize(12);
    pdf.setTextColor(255, 0, 0);
    pdf.text(`Error rendering section: ${error.message}`, margin, 30);
    pdf.setTextColor(0, 0, 0);
    throw error;
  } finally {
    if (root) {
      root.unmount();
    }
    if (container) {
      container.innerHTML = '';
    }
  }
}

export default renderReactSection;
